<template>
  <PageContainer size="large">
    <View direction="column" gap="sm">
      <PageBackNav
        :teleport-slot-id="headerTeleportSlot"
        :desktop-back-title="'Back to Application'"
        @back-nav="goBack()"
      />

      <TransUnionViewReport
        :is-loading="reportRef === undefined && !errorRef"
        :report="reportRef"
        :type="reportType"
        :format="reportFormat"
        :error="errorRef"
      />
    </View>
  </PageContainer>
</template>

<script setup lang="ts">
import { AxiosError } from 'axios';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { ReportFormat, ScreeningProductType, TransUnionViewReport } from '@tallo/applications';
import { PageBackNav, PageContainer, View } from '@tallo/design-system';
import { ApplicationsService } from '@tallo/investor/applications';
import { useNavigation } from '@tallo/investor/navigation';

import { headerTeleportSlot } from '../root/header-teleport-slot.const';

const route = useRoute();
const { goBack } = useNavigation();

const reportType = computed(() => route.params.reportType as ScreeningProductType);
const applicationId = computed(() => route.params.applicationId as string);
const requestedFormat = computed<ReportFormat>(() => (route.query.format === 'pdf' ? 'pdf' : 'html'));

const reportRef = ref<string | undefined>(undefined);
const reportFormat = ref<ReportFormat>('html');
const errorRef = ref<string | undefined>(undefined);

function releaseObjectUrl() {
  if (reportFormat.value === 'pdf' && reportRef.value) {
    URL.revokeObjectURL(reportRef.value);
  }
}

async function loadPdf(applicationId: string, reportType: ScreeningProductType) {
  reportRef.value = await ApplicationsService.getReportPdf(applicationId, reportType);
  reportFormat.value = 'pdf';
}

watch(
  () => ({ reportType: reportType.value, applicationId: applicationId.value, format: requestedFormat.value }),
  async ({ reportType, applicationId, format }) => {
    if (!reportType || !applicationId) {
      return;
    }

    releaseObjectUrl();
    reportRef.value = undefined;
    errorRef.value = undefined;

    try {
      if (format === 'pdf') {
        await loadPdf(applicationId, reportType);
        return;
      }

      const { data } = await ApplicationsService.getReport(applicationId, reportType);
      reportRef.value = data;
      reportFormat.value = 'html';
    } catch (error) {
      // A deep link without the format: the API answers 409 for a PDF-only report — fetch it as such.
      if (error instanceof AxiosError && error.response?.data?.code === 'REPORT_FORMAT_PDF') {
        await loadPdf(applicationId, reportType);
        return;
      }

      errorRef.value =
        error instanceof AxiosError
          ? (error.response?.data?.message ?? 'An error occurred while fetching the report')
          : 'An error occurred while fetching the report';
    }
  },
  { immediate: true },
);

onBeforeUnmount(releaseObjectUrl);
</script>

<style scoped lang="scss"></style>
